import Dexie, { Table } from "dexie";
import { fs, storage } from "../firebase.ts";
import {
    collection,
    addDoc,
    serverTimestamp,
    getDocs,
    query,
    where,
    arrayUnion,
    doc,
    updateDoc,
    getDoc,
    limit,
    orderBy,
    increment
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { Crypto } from "./Crypto.ts";
import { Jewelry, Diamonds } from "./Diamonds.ts";
import { Media } from "../managers/Media.ts";

// can be both locker and safe
export interface Chest {
    id: string; // for fast lookup and delete
    date: Date; // for ordering
    name: string; // treasure ship
    uid: string; // not logged in = "" // minter // TODO: later add uid of anon user if not logged in?? no still need device id
    data: string; // B protocal: location+silver + cover art + ar // can extract all data // cover art: {}/category/item OR {}/fileID
    hash: string; // secondary id // when minting, can create duplicates; when mining, cannot create duplicates
    address: string; // from public key: for filtering by locker
    location: { a: string; t: number; g: number } | null; // null = public; decrypted in case of safe; if from sender, duplicate public bc good for search??
    diamonds?: Array<number>; // array of id // decrypted
    message: string | null; // null = public; decrypted in case of safe; if from sender, duplicate public bc good for search??
    voice?: { l?: string; f?: File }; // decrypted
    coverPoster: string; // required: cover art: url to github or firebase storage // .jpg for now but can be different later
    coverVideo: string; // cover art: url to github or firebase storage // .mov for now but can be different later
    x?: Record<string, any>; // AR data // TODO: later more precise data structure
    //isStamp?: boolean; // for cloud only // deprecated bc can link multiple
    type: "minting" | "mint" | "mine"; // minting = 1 draft

    code?: Blob; // stored here: for cloud, code
    avatar?: Blob; // stored in cloud: for cloud
    crystal?: Blob; // dynamically generated: for cloud, code, crystal

    // mining
    devices?: Array<string>; // array of device id // when lockers // needed for hb-mine query
    users?: Array<string>; // array of uid // when safes // needed for hb-mine query
    recipients?: Array<string>; // array of public key // lockers and safes // needed for hb-vault rendering // identical to data.t

    // stats: from here
    views?: number; // total views
    digs?: number; // total scans here
    diggers?: Array<string>; // array of device id created here at /mine // unique devices // needed for hb-vault query
    miners?: Array<string>; // aray of uid: created here at /mine // unique users // needed for hb-vault query

    // stats: from b app: TODO: maybe too large => create subcollection instead; can add mined date; also other properties!
    previews?: number; // total: created at B.333.eco server
    crawlers?: Array<string>; // array of ip: created at B.333.eco server // unique crawlers
    scans?: number; // total: created at B.333.eco client // NOT redudant with scanners.count bc no duplicates
    scanners?: Array<string>; // array of anon uid: created at B.333.eco client // unique scanners
    // arrays of ips collected at B app // added via server for security
}

export class Chests extends Dexie {
    private media = new Media();
    private crypto = new Crypto();
    private diamonds = new Diamonds();
    private chests: Table<Chest>;

    private limit = 10;

    constructor() {
        super("Chests");
        this.version(1).stores({
            chests: "id, date, uid, type, address, hash, recipients, [hash+type], [uid+type], [uid+type+address], [uid+type+date], [uid+type+address+date], [uid+type+recipients+date]"
        });
        this.chests = this.table("chests");
    }

    async incrementViews(id: string) {
        const docRef = doc(fs, "chests", id);
        await updateDoc(docRef, {
            views: increment(1)
        });
    }

    async incrementDigs(id: string) {
        const docRef = doc(fs, "chests", id);
        await updateDoc(docRef, {
            digs: increment(1)
        });
    }

    async getByHash(
        hash: string,
        type: "mint" | "mine"
    ): Promise<Chest | null> {
        return (await this.chests.get({ hash, type })) || null;
    }

    async get(id: string): Promise<Chest | null> {
        return (await this.getLocal(id)) || (await this.getCloud(id));
    }

    getLocal(id: string): Promise<Chest | undefined> {
        return this.chests.get(id) || null;
    }

    async getCloud(id: string): Promise<Chest | null> {
        const docRef = doc(fs, "chests", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            data.id = docSnap.id;
            data.date = data.date.toDate();
            return data as Chest;
        } else return null;

        //return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    }

    async getDraft(): Promise<Chest | undefined> {
        return this.chests.get({ type: "minting" }) || null;
    }

    // local = no blockchain: chest can be deleted
    // store qr code or crystal
    async mint(
        name: string,
        uid: string,
        data: string, // B protocal
        address: string,
        location: { a: string; t: number; g: number } | null,
        message: string | null,
        coverPoster: string,
        coverVideo: string,
        type: "minting" | "mint" | "mine",
        id?: string
    ): Promise<string> {
        // verify signature before adding via mine via UI: done

        // check for duplicates by checking for hash, then return null for error ??
        // maybe allow duplicates bc part of new convo ?? yes

        const chest = {
            date: new Date(),
            name,
            uid,
            data,
            hash: await this.crypto.hash(data),
            address,
            location,
            message,
            coverPoster,
            coverVideo,
            type
        };

        if (id) {
            await this.chests.update(id, chest);
            return id;
        } else
            return this.chests.add({
                id: window.crypto.randomUUID(),
                ...chest
            }); // returns id
    }

    async rename(id: string, name: string, isCloud: boolean) {
        if (isCloud) {
            const chestDocRef = doc(fs, "chests", id);
            await updateDoc(chestDocRef, { name });
        } else {
            await this.chests.update(id, { name });
        }
    }

    // TODO: later in step 4: add x
    //async updateX(id: string) {}

    async updateVoice(id: string, voice: Chest["voice"]) {
        await this.chests.update(id, {
            voice
        });
    }

    async addDiamond(id: string, jewelry: Jewelry) {
        const chest = await this.chests.get(id);
        const did = await this.diamonds.add(jewelry);
        await this.chests.update(id, {
            diamonds: chest?.diamonds ? [...chest.diamonds, did] : [did]
        });
    }

    async update(id: string, code: Blob) {
        await this.chests.update(id, { code });
    }

    async updateCloud(chest: Chest, code: Blob, avatar: Blob) {
        const codeFile = new File([code], "code.B.gif", {
            type: "image/gif"
        });
        const codeUrl = await this.store(
            chest.id,
            chest.uid,
            `${chest.name}.B.gif`,
            codeFile,
            (progress) => console.log("Code: " + progress + "% done")
        );

        const avatarFile = new File([avatar], "avatar.B.gif", {
            type: "image/gif"
        });
        const avatarUrl = await this.store(
            chest.id,
            chest.uid,
            `${chest.name}.B.gif`,
            avatarFile,
            (progress) => console.log("Avatar: " + progress + "% done")
        );

        const chestDocRef = doc(fs, "chests", chest.id);
        await updateDoc(chestDocRef, { code: codeUrl, avatar: avatarUrl });
    }

    private store(
        id: string,
        uid: string,
        name: string,
        file: File,
        progressCallback?: (progress: number) => void
    ): Promise<string> {
        return new Promise((resolve, reject) => {
            const storageRef = ref(
                storage,
                `users/${uid}/chests/${id}/${file.name}`
            );
            const uploadTask = uploadBytesResumable(storageRef, file, {
                customMetadata: { name }
            });

            // native metadata
            // const metadata = await getMetadata(storageRef);
            // console.log(metadata.name);        // "img1.jpg"
            // console.log(metadata.size);        // size in bytes
            // console.log(metadata.contentType); // MIME type
            // console.log(metadata.timeCreated); // "2025-09-16T19:25:14.123Z"
            // console.log(metadata.updated);     // last update time
            // console.log(metadata.generation);  // storage generation ID
            // console.log(metadata.md5Hash);     // MD5 hash (if available)
            // console.log(metadata.bucket);      // bucket name
            // console.log(metadata.fullPath);    // "users/abc/img1.jpg"

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = Math.round(
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    );
                    //console.log("Upload is " + progress + "% done");
                    if (progressCallback) progressCallback(progress);
                },
                (error) => {
                    //console.error("Upload failed: ", error);
                    reject(error);
                },
                // Handle successful uploads on complete
                async () => {
                    try {
                        //console.log("Upload complete, getting download URL...");
                        const downloadURL = await getDownloadURL(
                            uploadTask.snapshot.ref
                        );
                        resolve(downloadURL);
                    } catch (error) {
                        //console.error("Failed to get download URL: ", error);
                        reject(error);
                    }
                }
            );
        });
    }

    //TODO: later store chests as blockchain with merkle tree // chest cannot be deleted
    // pro need cloud function for hb signing
    async cloudMint(
        name: string,
        uid: string,
        data: string,
        address: string,
        location: { a: string; t: number; g: number } | null,
        message: string | null,
        coverPoster: string,
        coverVideo: string
        //isStamp: boolean
    ): Promise<string> {
        const chestColRef = collection(fs, "chests");

        // check for duplicates??: check to see if hash already exists, return null for error:
        // allow duplicates bc maybe part of new convo

        // TEMP
        const newDoc = await addDoc(chestColRef, {
            date: serverTimestamp(),
            name,
            uid,
            data,
            hash: await this.crypto.hash(data), //TODO: blockchain instead ??
            address,
            location,
            message,
            coverPoster,
            coverVideo
            //isStamp
        });

        return newDoc.id;
    }

    // decrypted
    async updateCloudVoice(id: string, voice: Chest["voice"]) {
        const chestDocRef = doc(fs, "chests", id);
        await updateDoc(chestDocRef, { voice });
    }

    // decrypted
    async addCloudDiamond(id: string, jewelry: Jewelry): Promise<string> {
        const chestDocRef = doc(fs, "chests", id);
        await updateDoc(chestDocRef, { diamonds: arrayUnion(jewelry) });
        return id;
    }

    // TODO: later in step 4: add x
    //async updateMint(id: string) {}

    async addDevice(id: string, did: string): Promise<string> {
        // verify signature before adding mine via UI / do local check first

        //TODO: do subcollection "/mines" // maybe just do at trove only
        // uid, date
        // add minted data and mined date to hb-vault

        //TODO: check for duplicate before adding, return null for error
        // later: need better security in firestore rules

        const chestDocRef = doc(fs, "chests", id);
        await updateDoc(chestDocRef, { devices: arrayUnion(did) }); // TEMP
        return id;
    }

    // TODO: prop need to do cloud function for this bc kiitos balance and aura score and verify signature again
    async addUser(id: string, uid: string): Promise<string> {
        // verify signature before adding mine via UI / do local check first

        //TODO: do subcollection "/mines" // maybe just do at trove only
        // uid, date
        // add minted data and mined date to hb-vault

        //TODO: check for duplicate before adding, return null for error

        const chestDocRef = doc(fs, "chests", id);
        await updateDoc(chestDocRef, { users: arrayUnion(uid) }); // TEMP
        return id;
    }

    async addRecipient(id: string, kid: string): Promise<string> {
        // verify signature before adding mine via UI / do local check first

        //TODO: do subcollection "/mines" // maybe just do at trove only
        // uid, date
        // add minted data and mined date to hb-vault

        //TODO: check for duplicate before adding, return null for error
        // later: need better security in firestore rules

        const chestDocRef = doc(fs, "chests", id);
        await updateDoc(chestDocRef, { recipients: arrayUnion(kid) }); // TEMP
        return id;
    }

    // stats
    async addDigger(id: string, did: string): Promise<string> {
        const chestDocRef = doc(fs, "chests", id);
        await updateDoc(chestDocRef, { diggers: arrayUnion(did) }); // TEMP: do subcollection instead?
        return id;
    }

    // stats
    async addMiner(id: string, uid: string): Promise<string> {
        const chestDocRef = doc(fs, "chests", id);
        await updateDoc(chestDocRef, { miners: arrayUnion(uid) }); // TEMP: do subcollection instead?
        return id;
    }

    async getMints(
        uid: string,
        date: string = "",
        address: string = ""
    ): Promise<Chest[]> {
        // Convert "2025-10-10" → start and end of that day // Removing the Z means the time is interpreted in the local timezone rather than UTC.
        const startOfDay = date ? new Date(`${date}T00:00:00`) : null;
        const endOfDay = date ? new Date(`${date}T23:59:59`) : null;

        if (uid) {
            let q;
            if (startOfDay && endOfDay && address) {
                q = query(
                    collection(fs, "chests"),
                    where("uid", "==", uid),
                    where("address", "==", address),
                    where("date", ">=", startOfDay),
                    where("date", "<=", endOfDay)
                    //orderBy("date", "desc")
                    //limit(this.limit)
                );
            } else if (startOfDay && endOfDay) {
                q = query(
                    collection(fs, "chests"),
                    where("uid", "==", uid),
                    where("date", ">=", startOfDay),
                    where("date", "<=", endOfDay)
                    //orderBy("date", "desc")
                    //limit(this.limit)
                );
            } else if (address) {
                q = query(
                    collection(fs, "chests"),
                    where("uid", "==", uid),
                    where("address", "==", address),
                    orderBy("date", "desc"),
                    limit(this.limit)
                );
            } else {
                q = query(
                    collection(fs, "chests"),
                    where("uid", "==", uid),
                    orderBy("date", "desc"),
                    limit(this.limit)
                );
            }

            const snapshot = await getDocs(q);
            const cloudChests: Chest[] = snapshot.docs.map((doc) => {
                const data = doc.data();
                data.id = doc.id;
                data.date = data.date.toDate();
                data.type = "mint";
                return data as Chest;
            });

            // const chests = await this.chests
            //     .where("[uid+type]")
            //     .anyOf(
            //         [
            //             [uid, "mint"],
            //             ["", "mine"]
            //         ]
            //     )
            //     .sortBy("date");

            // const localChests = await this.chests
            //     .where(address ? "[uid+type+address]" : "[uid+type]")
            //     .equals(address ? [uid, "mint", address] : [uid, "mint"])
            //     .toArray();
            const localChests = this.chests
                .where(address ? "[uid+type+address+date]" : "[uid+type+date]")
                .between(
                    address
                        ? [uid, "mint", address, startOfDay || Dexie.minKey]
                        : [uid, "mint", startOfDay || Dexie.minKey],
                    address
                        ? [uid, "mint", address, endOfDay || Dexie.maxKey]
                        : [uid, "mint", endOfDay || Dexie.maxKey]
                )
                .reverse(); // descending order
            if (!date) localChests.limit(this.limit);

            // const localChests2 = await this.chests
            //     .where(address ? "[uid+type+address]" : "[uid+type]")
            //     .equals(address ? ["", "mint", address] : ["", "mint"])
            //     .toArray();
            const localChests2 = this.chests
                .where(address ? "[uid+type+address+date]" : "[uid+type+date]")
                .between(
                    address
                        ? ["", "mint", address, startOfDay || Dexie.minKey]
                        : ["", "mint", startOfDay || Dexie.minKey],
                    address
                        ? ["", "mint", address, endOfDay || Dexie.maxKey]
                        : ["", "mint", endOfDay || Dexie.maxKey]
                )
                .reverse(); // descending order
            if (!date) localChests2.limit(this.limit);

            const combined = [
                ...cloudChests,
                ...(await localChests.toArray()),
                ...(await localChests2.toArray())
            ];
            combined.sort((a, b) => {
                const da = a.date ? a.date.getTime() : 0;
                const dbt = b.date ? b.date.getTime() : 0;
                return dbt - da;
            });

            return combined;
        } else {
            // const chests = await this.chests
            //     .where(address ? "[uid+type+address]" : "[uid+type]")
            //     .equals(address ? ["", "mint", address] : ["", "mint"])
            //     .sortBy("date");
            // return chests.reverse();
            //return await this.chests.orderBy("date").reverse().toArray();

            const chests = this.chests
                .where(address ? "[uid+type+address+date]" : "[uid+type+date]")
                .between(
                    address
                        ? ["", "mint", address, startOfDay || Dexie.minKey]
                        : ["", "mint", startOfDay || Dexie.minKey],
                    address
                        ? ["", "mint", address, endOfDay || Dexie.maxKey]
                        : ["", "mint", endOfDay || Dexie.maxKey]
                )
                .reverse(); // descending order
            if (!date) chests.limit(this.limit);

            return await chests.toArray();
        }
    }

    async getMines(
        did: string,
        uid: string,
        date: string = "",
        address: string = ""
    ): Promise<Chest[]> {
        // Convert "2025-10-10" → start and end of that day
        const startOfDay = date ? new Date(`${date}T00:00:00Z`) : null;
        const endOfDay = date ? new Date(`${date}T23:59:59Z`) : null;

        let q;
        if (startOfDay && endOfDay && address) {
            q = query(
                collection(fs, "chests"),
                //where("diggers", "array-contains", did),
                where("recipients", "array-contains", address),
                where("date", ">=", startOfDay),
                where("date", "<=", endOfDay)
                //orderBy("date", "desc")
                //limit(this.limit)
            );
        } else if (startOfDay && endOfDay) {
            q = query(
                collection(fs, "chests"),
                where("diggers", "array-contains", did),
                where("date", ">=", startOfDay),
                where("date", "<=", endOfDay)
                //orderBy("date", "desc")
                //limit(this.limit)
            );
        } else if (address) {
            q = query(
                collection(fs, "chests"),
                //where("diggers", "array-contains", did),
                where("recipients", "array-contains", address),
                orderBy("date", "desc"),
                limit(this.limit)
            );
        } else {
            q = query(
                collection(fs, "chests"),
                where("diggers", "array-contains", did),
                orderBy("date", "desc"),
                limit(this.limit)
            );
        }

        const snapshot = await getDocs(q);
        const cloudChests: Chest[] = snapshot.docs.map((doc) => {
            const data = doc.data();
            data.id = doc.id;
            data.date = data.date.toDate(); // minted date
            data.type = "mine";
            return data as Chest;
        });

        // const localChests = await this.chests
        //     .where(address ? "[uid+type+address]" : "[uid+type]")
        //     .equals(address ? ["", "mine", address] : ["", "mine"])
        //     .toArray();
        const localChests = this.chests
            .where(address ? "[uid+type+recipients+date]" : "[uid+type+date]")
            .between(
                address
                    ? ["", "mine", address, startOfDay || Dexie.minKey]
                    : ["", "mine", startOfDay || Dexie.minKey],
                address
                    ? ["", "mine", address, endOfDay || Dexie.maxKey]
                    : ["", "mine", endOfDay || Dexie.maxKey]
            )
            .reverse(); // descending order
        if (!date) localChests.limit(this.limit);

        let combined: Chest[] = [];
        if (uid) {
            let q2;
            if (startOfDay && endOfDay && address) {
                q2 = query(
                    collection(fs, "chests"),
                    //where("miners", "array-contains", uid),
                    where("recipients", "array-contains", address),
                    where("date", ">=", startOfDay),
                    where("date", "<=", endOfDay)
                    //orderBy("date", "desc")
                    //limit(this.limit)
                );
            } else if (startOfDay && endOfDay) {
                q2 = query(
                    collection(fs, "chests"),
                    where("miners", "array-contains", uid),
                    where("date", ">=", startOfDay),
                    where("date", "<=", endOfDay)
                    //orderBy("date", "desc")
                    //limit(this.limit)
                );
            } else if (address) {
                q2 = query(
                    collection(fs, "chests"),
                    //where("miners", "array-contains", uid),
                    where("recipients", "array-contains", address),
                    where("address", "==", address),
                    orderBy("date", "desc"),
                    limit(this.limit)
                );
            } else {
                q2 = query(
                    collection(fs, "chests"),
                    where("miners", "array-contains", uid),
                    orderBy("date", "desc"),
                    limit(this.limit)
                );
            }

            const snapshot2 = await getDocs(q2);
            const cloudChests2: Chest[] = snapshot2.docs.map((doc) => {
                const data = doc.data();
                data.id = doc.id;
                data.date = data.date.toDate(); // minted date
                data.type = "mine";
                return data as Chest;
            });

            // const localChests2 = await this.chests
            //     .where(address ? "[uid+type+address]" : "[uid+type]")
            //     .equals(address ? [uid, "mine", address] : [uid, "mine"])
            //     .limit(this.limit)
            //     .toArray();
            const localChests2 = this.chests
                .where(
                    address ? "[uid+type+recipients+date]" : "[uid+type+date]"
                )
                .between(
                    address
                        ? [uid, "mine", address, startOfDay || Dexie.minKey]
                        : [uid, "mine", startOfDay || Dexie.minKey],
                    address
                        ? [uid, "mine", address, endOfDay || Dexie.maxKey]
                        : [uid, "mine", endOfDay || Dexie.maxKey]
                )
                .reverse(); // descending order
            if (!date) localChests2.limit(this.limit);

            combined = [
                ...cloudChests,
                ...cloudChests2,
                ...(await localChests.toArray()),
                ...(await localChests2.toArray())
            ];
        } else {
            combined = [...cloudChests, ...(await localChests.toArray())];
        }

        combined.sort((a, b) => {
            const da = a.date ? a.date.getTime() : 0;
            const dbt = b.date ? b.date.getTime() : 0;
            return dbt - da;
        });

        return combined;
    }

    // delete is reserved word
    async remove(id: string): Promise<void> {
        await this.media.deleteFolderFromOPFS(`chests/${id}`);
        const chest = await this.chests.get(id);
        if (chest?.diamonds)
            for (const diamond of chest.diamonds)
                await this.diamonds.remove(diamond);
        return this.chests.delete(id);
    }

    async removeAll(type: "mint" | "mine"): Promise<number> {
        const chests = await this.chests.where("type").equals(type).toArray();
        for (const chest of chests) {
            await this.media.deleteFolderFromOPFS(`chests/${chest.id}`);
            if (chest.diamonds)
                for (const diamond of chest.diamonds)
                    await this.diamonds.remove(diamond);
        }
        return this.chests.where("type").equals(type).delete(); // returns number of deleted items
    }

    // clear all: not used
    async clear(): Promise<void> {
        await this.media.deleteFolderFromOPFS("chests");
        await this.diamonds.clear();
        return this.chests.clear();
    }

    async count(type: "mint" | "mine" | null = null): Promise<number> {
        switch (type) {
            case "mint":
                return this.chests.where("type").equals("mint").count();
            case "mine":
                return this.chests.where("type").equals("mine").count();
            default:
                return this.chests.count();
        }
    }
}
